import {
  INVOICE_DATE_FILTER,
  INVOICE_ELIGIBLE_ISLEM_TURU,
  INVOICE_LIST_PAGE_SIZE,
  INVOICE_LIST_SHOW_ALL,
  INVOICE_MATCH_STATUS,
  INVOICE_MATCH_THRESHOLDS
} from '../../utils/invoiceSchema';
import { resolveMovementDetail, isInternalOwnerBankTransfer } from '../../utils/bankingMovementUi';
import {
  isBankFeeMovement,
  isCashDepositMovement,
  isMarketplacePayoutMovement,
  isPosSettlementMovement
} from '../../utils/bankMovementFees';
import { getIstanbulDayKey, shiftIstanbulDayKey } from '../../utils/bankingDateTime';

const EMPTY_CUSTOMER_MARKERS = new Set(['-', '—', 'yok', 'null', 'undefined', 'bos', 'boş']);

const normalizeCustomerText = (value) => {
  const text = String(value ?? '').trim();
  if (!text) return '';
  if (EMPTY_CUSTOMER_MARKERS.has(text.toLocaleLowerCase('tr-TR'))) return '';
  return text;
};

const AMOUNT_EXACT = 40;
const DATE_SAME_DAY = 15;
const DATE_ONE_DAY = 6;
const DATE_THREE_DAYS = 2;
const TIME_ONE_HOUR = 10;
const TIME_THREE_HOURS = 5;
const TIME_SIX_HOURS = 2;
const NAME_STRONG_SCORE = 35;
const NAME_MEDIUM_SCORE = 25;

export const NAME_MATCH_LEVEL = {
  STRONG: 'strong',
  MEDIUM: 'medium',
  WEAK: 'weak',
  /** İşlemde müşteri adı girilmemiş ("1", "1 1", tarih vb.) — çelişki değil, bilgi yok. */
  UNKNOWN: 'unknown',
  NONE: 'none'
};

const LEGAL_SUFFIX_PATTERN =
  /\b(a\.?\s*ş\.?|a\.?\s*s\.?|ltd\.?|şti\.?|sti\.?|san\.?|tic\.?|t\.?c\.?|koop\.?|kuyumculuk)\b/gi;

const STOP_WORDS = new Set(['ve', 'ile', 'icin', 'the', 'and', 'bir', 'ya', 'da']);

const GENERIC_WEAK_TOKENS = new Set([
  'cam',
  'cami',
  'merkez',
  'sube',
  'subesi',
  'mah',
  'mahalle',
  'no',
  'tel',
  'gsm'
]);

const ORG_SIGNAL_TOKENS = new Set(['dernek', 'dernegi', 'vakif', 'vakfi', 'foundation', 'association']);

const NOISE_NAME_TOKENS = new Set([
  'as',
  'ltd',
  'san',
  'tic',
  'tc',
  'bank',
  'bankasi',
  'banka',
  'masraf',
  'komisyon',
  'vakif',
  'vakfi',
  'kooperatif',
  'sirketi',
  'anonim',
  'fast',
  'odeme',
  'havale',
  'eft',
  'gelen',
  'giden',
  'ucreti',
  'bedeli',
  'merkez',
  'sube',
  'subesi',
  'limited',
  'inc',
  'gmbh',
  'kuyumculuk',
  'yaptirma',
  'yasatma'
]);

/** Türkçe uyumlu normalize (büyük/küçük harf, boşluk). */
export const normalizeTr = (value) =>
  String(value || '')
    .toLocaleLowerCase('tr-TR')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

export const resolveFirestoreDate = (value) => {
  if (!value) return null;
  if (typeof value?.toDate === 'function') return value.toDate();
  if (typeof value === 'object') {
    const sec = value.seconds ?? value._seconds;
    if (sec != null && Number.isFinite(Number(sec))) {
      const ns = Number(value.nanoseconds ?? value._nanoseconds ?? 0);
      return new Date(Number(sec) * 1000 + Math.floor(ns / 1e6));
    }
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

export const formatMusteriAdi = (musteri) => {
  if (!musteri) return '';
  if (typeof musteri === 'string') return normalizeCustomerText(musteri);
  const fromParts = `${musteri.isim || ''} ${musteri.soyisim || ''}`.trim();
  if (fromParts) return fromParts;
  return normalizeCustomerText(musteri.ad || musteri.name || musteri.musteriAdi || musteri.unvan);
};

/** İşlem kaydındaki müşteri adını güvenli çözümler. */
export const resolveIslemMusteriName = (islem) => {
  const direct = formatMusteriAdi(islem?.musteri);
  if (direct) return direct;
  return normalizeCustomerText(
    islem?.musteriInput || islem?.musteriAdi || islem?.musteriAd || islem?.musteriIsim
  );
};

export const hasValidIslemMusteri = (islem) => Boolean(resolveIslemMusteriName(islem));

export const hasIslemFirma = (islem) => {
  const firma = islem?.firma;
  if (!firma) return false;
  if (typeof firma === 'string') return Boolean(normalizeCustomerText(firma));
  return Boolean(normalizeCustomerText(firma.firmaIsmi || firma.ad || firma.isim || firma.unvan));
};

/** Gelen banka hareketi mi. */
export const isIncomingBankMovement = (movement) => {
  const direction = String(movement?.direction || '').trim().toLowerCase();
  if (direction === 'in') return true;
  if (direction === 'out') return false;
  return Number(movement?.amount) > 0;
};

export const filterIncomingBankMovements = (movements, { includeAll = INVOICE_LIST_SHOW_ALL } = {}) => {
  const list = movements || [];
  if (includeAll) return list;
  return list.filter(
    (movement) =>
      isIncomingBankMovement(movement) &&
      !isInternalOwnerBankTransfer(movement) &&
      // Banka masrafı/kesintisi bir müşteri ödemesi değildir; faturalandırılmaz.
      !isBankFeeMovement(movement) &&
      // Trendyol vb. pazaryeri hakedişi de mağaza satışı değildir.
      !isMarketplacePayoutMovement(movement) &&
      // POS günsonu: kredi kartı tahsilatının bankaya yatan karşılığı, ayrı fatura yok.
      !isPosSettlementMovement(movement) &&
      // ATM / gişeden nakit yatırma: mağazanın kendi nakdi, müşteri ödemesi değil.
      !isCashDepositMovement(movement)
  );
};

/** Fatura eşleştirmeye uygun işlem mi (Satış/Giriş + müşteri, firma-only hariç). */
export const isInvoiceEligibleIslem = (islem) => {
  if (Number(islem?.havaleEft) <= 0) return false;

  const islemTuru = String(islem?.islemTuru || '').trim();
  if (!INVOICE_ELIGIBLE_ISLEM_TURU.includes(islemTuru)) return false;

  if (!hasValidIslemMusteri(islem)) return false;

  // Sadece müşteri işlemleri; firma kayıtları ve firma+müşteri karışık kayıtlar hariç.
  if (hasIslemFirma(islem)) return false;

  return true;
};

export const filterInvoiceEligibleIslemler = (islemler, { includeAll = INVOICE_LIST_SHOW_ALL } = {}) => {
  const list = islemler || [];
  if (includeAll) return list.filter((row) => Number(row?.havaleEft) > 0);
  return list.filter(isInvoiceEligibleIslem);
};

const IBAN_REGEX = /\bTR\d{2}(?:\s?\d{4}){5}\s?\d{2}\b/i;

const toJsDateFromDayKey = (dayKey) => {
  const [y, m, d] = String(dayKey || '').split('-').map(Number);
  return new Date(Date.UTC(y, (m || 1) - 1, d || 1));
};

/** Tarih filtresi için başlangıç/bitiş (İstanbul takvim günü, yyyy-mm-dd). */
export const getInvoiceDateRange = (filterKey, now = new Date()) => {
  if (!filterKey || filterKey === INVOICE_DATE_FILTER.ALL) return null;

  const todayKey = getIstanbulDayKey(now);
  if (!todayKey) return null;

  if (filterKey === INVOICE_DATE_FILTER.TODAY) {
    return { startKey: todayKey, endKey: todayKey };
  }

  if (filterKey === INVOICE_DATE_FILTER.YESTERDAY) {
    const yKey = shiftIstanbulDayKey(todayKey, -1);
    return { startKey: yKey, endKey: yKey };
  }

  if (filterKey === INVOICE_DATE_FILTER.LAST_7_DAYS) {
    return { startKey: shiftIstanbulDayKey(todayKey, -6), endKey: todayKey };
  }

  if (filterKey === INVOICE_DATE_FILTER.LAST_15_DAYS) {
    return { startKey: shiftIstanbulDayKey(todayKey, -14), endKey: todayKey };
  }

  if (filterKey === INVOICE_DATE_FILTER.THIS_WEEK) {
    const d = toJsDateFromDayKey(todayKey);
    const weekday = d.getUTCDay();
    const mondayOffset = weekday === 0 ? 6 : weekday - 1;
    return { startKey: shiftIstanbulDayKey(todayKey, -mondayOffset), endKey: todayKey };
  }

  if (filterKey === INVOICE_DATE_FILTER.THIS_MONTH) {
    const [y, m] = todayKey.split('-');
    return { startKey: `${y}-${m}-01`, endKey: todayKey };
  }

  return null;
};

export const isDateWithinInvoiceRange = (date, range) => {
  if (!range) return true;
  if (!date) return false;
  const key = getIstanbulDayKey(date);
  if (!key) return false;
  if (range.startKey && range.endKey) {
    return key >= range.startKey && key <= range.endKey;
  }
  const time = date.getTime();
  return time >= range.start.getTime() && time <= range.end.getTime();
};

export const resolveMovementListDate = (movement) =>
  resolveFirestoreDate(movement?.bookingDate || movement?.createdAt);

/** Fatura eşleşme kaydı için liste tarihi — işlem günü öncelikli (valör tarihi değil). */
export const resolveDraftMatchDate = (match) => {
  const islemTs = resolveFirestoreDate(match?.islemData?.timestamp);
  if (islemTs) return islemTs;

  if (match?.matchType === 'multi' && Array.isArray(match?.islemlerData) && match.islemlerData.length) {
    const timestamps = match.islemlerData
      .map((row) => resolveFirestoreDate(row?.timestamp))
      .filter(Boolean)
      .sort((a, b) => b.getTime() - a.getTime());
    if (timestamps[0]) return timestamps[0];
  }

  const draftDate = resolveFirestoreDate(match?.invoiceDraft?.invoiceDate);
  if (draftDate) return draftDate;

  const bank = match?.bankMovementData || {};
  return resolveFirestoreDate(
    bank.bookingDate || bank.activityDate || bank.createdAt || match?.createdAt
  );
};

export const resolveIslemListDate = (islem) => resolveFirestoreDate(islem?.timestamp);

export const filterItemsByInvoiceDate = (items, getDate, filterKey) => {
  const range = getInvoiceDateRange(filterKey);
  if (!range) return items || [];
  return (items || []).filter((item) => isDateWithinInvoiceRange(getDate(item), range));
};

export const paginateInvoiceList = (items, page, pageSize = INVOICE_LIST_PAGE_SIZE) => {
  const list = items || [];
  const total = list.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize) || 1);
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * pageSize;

  return {
    items: list.slice(start, start + pageSize),
    total,
    totalPages,
    page: safePage,
    pageSize,
    from: total === 0 ? 0 : start + 1,
    to: Math.min(start + pageSize, total)
  };
};

const pickRawMovementField = (rawData, keys) => {
  if (!rawData || typeof rawData !== 'object') return '';
  for (const key of keys) {
    const value = rawData[key];
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      return String(value).trim();
    }
  }
  return '';
};

const stripDescriptionNoise = (text) =>
  String(text || '')
    .replace(/\([^)]*\)/g, ' ')
    .replace(/elden teslim (al[ıi]nan|edilen) k[ıi]ymetli maden (al[ıi]m|sat[ıi][şs]) bedeli/gi, ' ')
    .replace(/k[ıi]ymetli maden (al[ıi]m|sat[ıi][şs]) bedeli/gi, ' ')
    .replace(/tcmb fast [üu]creti[\s\S]*?tutar[ıi](n[ıi]n)?/gi, ' ')
    .replace(/\bfast [öo]demesi\b/gi, ' ')
    .replace(/\b(virman|referans|nolu|no'lu|numaral[ıi]|giden|gelen)\b/gi, ' ')
    .replace(/\b\d[\d./:-]{4,}\b/g, ' ')
    .replace(IBAN_REGEX, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const cleanParsedSenderName = (raw) => {
  const cleaned = stripDescriptionNoise(raw).replace(/^[-–:,\s]+|[-–:,\s]+$/g, '').trim();
  if (!cleaned) return '';
  const personTokens = extractPersonTokensFromSender(cleaned);
  if (personTokens.length >= 2) return personTokens.join(' ');
  const words = cleaned.split(' ').filter(Boolean);
  if (words.length > 4) return words.slice(-4).join(' ');
  return cleaned;
};

/** Banka açıklamasından gönderen adı ayıklar (IBAN öncesi / hesabından öncesi). */
export const parseSenderFromDescription = (description) => {
  const text = String(description || '').replace(/\s+/g, ' ').trim();
  if (!text) return '';

  const ibanMatch = text.match(IBAN_REGEX);
  if (ibanMatch) {
    const beforeIban = text.slice(0, text.indexOf(ibanMatch[0]));
    const parsed = cleanParsedSenderName(beforeIban);
    if (parsed) return parsed;
  }

  const fromAccountMatch = text.match(/hesab[ıi]ndan/i);
  if (fromAccountMatch) {
    const beforeAccount = text.slice(0, fromAccountMatch.index);
    const parsed = cleanParsedSenderName(beforeAccount);
    if (parsed) return parsed;
  }

  const cleaned = cleanParsedSenderName(text);
  const words = cleaned.split(' ').filter((word) => word.length >= 2);
  if (words.length >= 2 && words.length <= 5) return words.join(' ');
  if (words.length > 5) return words.slice(0, 4).join(' ');

  return '';
};

/**
 * Açıklamadaki tire ayraçlı bölümlerde geçen kişi adı adayları.
 *
 * Garanti açıklaması "FAST3828670223-BÜŞRA ALTINTAŞ-ELDEN TESLİM EDİLEN
 * KIYMETLİ MADEN ALIM BEDELİ" biçiminde geliyor. Havaleyi hesap sahibi
 * (ör. MEHMET DEMİRSOY) gönderse de malın sahibi ortadaki isimdir; personel
 * satışı o isme kaydettiği için eşleşme kuruluyordu (10.10.2026 Sefaköy).
 *
 * Yalnızca 2-4 kelimelik, rakamsız ve banka terimi içermeyen bölümler aday
 * sayılır; "CEP ŞUBE", "KESİNTİ VE EKLERİ", "ALTIN ALIM BEDELİ" elenir.
 */
const DESCRIPTION_NAME_NOISE = new Set(
  [
    'fast', 'eft', 'havale', 'hvl', 'virman', 'transfer', 'ödeme', 'ödemesi',
    'bedel', 'bedeli', 'teslim', 'edilen', 'alınan', 'kıymetli', 'maden',
    'alım', 'satış', 'kesinti', 'ekleri', 'bonus', 'komisyon', 'masraf',
    'ücret', 'ücreti', 'şube', 'şubesi', 'cep', 'pos', 'iade', 'faiz',
    'vergi', 'kredi', 'borç', 'altın', 'gümüş', 'müşteri', 'hesap',
    'hesabı', 'referans', 'tcmb', 'bakiye', 'nakit', 'tahsilat'
  ].map((word) => normalizeTr(word))
);

export const parseCustomerNamesFromDescription = (description) => {
  const text = String(description || '').replace(/\s+/g, ' ').trim();
  if (!text) return [];

  const out = [];
  text.split(/[-–—]/).forEach((part) => {
    const parca = part.trim();
    if (!parca) return;
    const tokens = extractMeaningfulNameTokens(parca);
    if (tokens.length < 2 || tokens.length > 4) return;
    if (tokens.some((token) => DESCRIPTION_NAME_NOISE.has(token) || /\d/.test(token))) return;
    const temiz = parca.replace(/\s+/g, ' ');
    if (!out.includes(temiz)) out.push(temiz);
  });
  return out;
};

export const UNKNOWN_MOVEMENT_SENDER = 'Gönderen Bilinmiyor';

export const resolveMovementSenderName = (movement) => {
  const direct = normalizeCustomerText(movement?.senderName || movement?.gonderenAdi);
  if (direct) return direct;

  const fromRaw = pickRawMovementField(movement?.rawData, [
    'senderName',
    'gonderenAdi',
    'gonderenAd',
    'gonderen',
    'karsiAd'
  ]);
  if (fromRaw) return fromRaw;

  const { senderName } = resolveMovementDetail(movement);
  if (senderName) return senderName;

  const parsed = parseSenderFromDescription(movement?.description);
  if (parsed) return parsed;

  return UNKNOWN_MOVEMENT_SENDER;
};

export const truncateSenderDisplay = (name, maxLength = 28) => {
  const text = String(name || '').trim();
  if (!text || text.length <= maxLength) return text;
  return `${text.slice(0, maxLength - 1)}…`;
};

export const customerNameTokens = (nameOrMusteri) => extractMeaningfulNameTokens(nameOrMusteri);

export const normalizePersonName = (value) => {
  let text = normalizeTr(value);
  text = text.replace(/\./g, ' ').replace(/[,;:/\\|()[\]{}'"`+]/g, ' ');
  text = text.replace(LEGAL_SUFFIX_PATTERN, ' ');
  text = text.replace(/\s+/g, ' ').trim();
  return text;
};

const isMeaningfulToken = (token) =>
  Boolean(token) && token.length >= 2 && !STOP_WORDS.has(token) && !NOISE_NAME_TOKENS.has(token);

export const extractMeaningfulNameTokens = (name) =>
  normalizePersonName(name).split(' ').filter(isMeaningfulToken);

/** "Bankaşi A.Ş. Bahar Yanık" gibi metinlerden kişi adını ayıklar. */
export const extractPersonTokensFromSender = (senderName) => {
  const tokens = extractMeaningfulNameTokens(senderName);
  if (tokens.length <= 3) return tokens;
  const tail = tokens.slice(-3);
  const hasOrgPrefix = tokens.some((token) => ORG_SIGNAL_TOKENS.has(token) || token.length > 8);
  if (hasOrgPrefix && tail.length >= 2) return tail.slice(-2);
  return tail;
};

const tokensEquivalent = (a, b) => {
  if (a === b) return true;
  const shorter = a.length <= b.length ? a : b;
  const longer = a.length <= b.length ? b : a;
  if (shorter.length >= 4 && (longer.startsWith(shorter) || longer.includes(shorter))) return true;
  return false;
};

const findMatchedTokens = (tokensA, tokensB) => {
  const matched = [];
  tokensA.forEach((a) => {
    if (tokensB.some((b) => tokensEquivalent(a, b))) matched.push(a);
  });
  return [...new Set(matched)];
};

const isOrgToken = (token) =>
  ORG_SIGNAL_TOKENS.has(token) || [...ORG_SIGNAL_TOKENS].some((org) => tokensEquivalent(token, org));

/**
 * İşlemde gerçek bir müşteri adı var mı.
 *
 * Geçmiş kayıtlarda ad alanına "1", "1 1", "28.10.25" gibi yer tutucular yazılmış.
 * Bunlar "yanlış isim" değil, "isim yok" demektir; eşleşmeyi reddetmek yerine
 * düşük güvenli ÖNERİ üretmeye uygundur.
 */
export const isPlaceholderCustomerName = (name) => {
  const raw = String(name || '').trim();
  if (!raw) return true;
  const letters = raw.replace(/[^A-Za-zÇĞİÖŞÜçğıöşü]/g, '');
  if (letters.length < 3) return true;
  return extractMeaningfulNameTokens(raw).length < 1;
};

export const isInvoiceAmountMatch = (bankAmount, islemAmount) => {
  const bank = Math.abs(Number(bankAmount) || 0);
  const islem = Math.abs(Number(islemAmount) || 0);
  return bank > 0 && islem > 0 && Math.abs(bank - islem) < 0.02;
};

/**
 * Banka gönderen adı ↔ işlem müşteri adı karşılaştırması.
 */
export const evaluateInvoiceNameMatch = (senderName, customerName) => {
  const senderNorm = normalizePersonName(senderName);
  const customerNorm = normalizePersonName(customerName);
  const unknownSender = normalizePersonName(UNKNOWN_MOVEMENT_SENDER);

  if (!senderNorm || !customerNorm || senderNorm === unknownSender) {
    return {
      nameMatch: false,
      nameMatchLevel: NAME_MATCH_LEVEL.NONE,
      nameScore: 0,
      strength: 'none'
    };
  }

  if (senderNorm === customerNorm) {
    return {
      nameMatch: true,
      nameMatchLevel: NAME_MATCH_LEVEL.STRONG,
      nameScore: NAME_STRONG_SCORE,
      strength: 'full'
    };
  }

  const senderAll = extractMeaningfulNameTokens(senderName);
  const senderPerson = extractPersonTokensFromSender(senderName);
  const customerAll = extractMeaningfulNameTokens(customerName);

  if (!senderAll.length || !customerAll.length) {
    return {
      nameMatch: false,
      nameMatchLevel: NAME_MATCH_LEVEL.NONE,
      nameScore: 0,
      strength: 'none'
    };
  }

  const matchedAll = findMatchedTokens(senderAll, customerAll);
  const matchedPerson = findMatchedTokens(senderPerson, customerAll);
  const matched = [...new Set([...matchedAll, ...matchedPerson])];

  if (!matched.length) {
    return {
      nameMatch: false,
      nameMatchLevel: NAME_MATCH_LEVEL.NONE,
      nameScore: 0,
      strength: 'none'
    };
  }

  const customerFullyMatched = customerAll.every((token) =>
    matchedAll.some((matchedToken) => tokensEquivalent(matchedToken, token))
  );
  const personFullyMatched =
    customerAll.length >= 2 &&
    customerAll.every((token) => matchedPerson.some((matchedToken) => tokensEquivalent(matchedToken, token)));

  if (customerFullyMatched || personFullyMatched || matched.length >= 2) {
    return {
      nameMatch: true,
      nameMatchLevel: NAME_MATCH_LEVEL.STRONG,
      nameScore: NAME_STRONG_SCORE,
      strength: 'full'
    };
  }

  const meaningfulMatched = matched.filter(
    (token) => token.length >= 4 && !GENERIC_WEAK_TOKENS.has(token)
  );
  const orgMatch = matched.some(isOrgToken);

  if (meaningfulMatched.length >= 1 || orgMatch) {
    return {
      nameMatch: true,
      nameMatchLevel: NAME_MATCH_LEVEL.MEDIUM,
      nameScore: NAME_MEDIUM_SCORE,
      strength: 'partial'
    };
  }

  if (matched.length === 1) {
    return {
      nameMatch: false,
      nameMatchLevel: NAME_MATCH_LEVEL.WEAK,
      nameScore: 0,
      strength: 'weak'
    };
  }

  return {
    nameMatch: false,
    nameMatchLevel: NAME_MATCH_LEVEL.NONE,
    nameScore: 0,
    strength: 'none'
  };
};

export const isInvoiceNameSuggestionEligible = (matchResult) => {
  const level = matchResult?.nameMatchLevel;
  return (
    level === NAME_MATCH_LEVEL.STRONG ||
    level === NAME_MATCH_LEVEL.MEDIUM ||
    // İşlemde ad yok: öneri olarak çıkar, otomatik eşleşmez (skor < 80).
    level === NAME_MATCH_LEVEL.UNKNOWN
  );
};

export const isInvoicePairEligibleForSuggestion = (matchResult) =>
  Boolean(matchResult?.amountMatch && isInvoiceNameSuggestionEligible(matchResult));

/* ------------------------------------------------------------------ *
 * Fatura arama
 *
 * Modülde hiç arama yoktu; personel bir faturayı ancak tarih + durum
 * filtreleriyle avlayabiliyordu. Aranan: müşteri adı, TCKN/VKN, tutar,
 * fatura numarası, işlem no ve banka gönderen adı.
 * ------------------------------------------------------------------ */

/**
 * Arama için Türkçe harf katlaması.
 * normalizeTr (eşleştirme motoru) NFD ile ğüşöç'ü çözüyor ama "ı" (U+0131)
 * ayrıştırılamadığı için "yilmaz" araması "Yılmaz" kaydını bulamıyordu.
 * Eşleştirme skorunu bozmamak için ayrı bir fonksiyon olarak tutuluyor.
 */
const TR_FOLD = {
  '\u0131': 'i', '\u0130': 'i',
  '\u015f': 's', '\u015e': 's',
  '\u011f': 'g', '\u011e': 'g',
  '\u00fc': 'u', '\u00dc': 'u',
  '\u00f6': 'o', '\u00d6': 'o',
  '\u00e7': 'c', '\u00c7': 'c'
};

export const normalizeSearchText = (value) =>
  String(value || '')
    .replace(/[\u0131\u0130\u015f\u015e\u011f\u011e\u00fc\u00dc\u00f6\u00d6\u00e7\u00c7]/g, (ch) => TR_FOLD[ch] || ch)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

/** Tutar araması: "12.500,50" · "12500.5" · "12500" hepsi eşleşir. */
const normalizeAmountQuery = (value) =>
  String(value || '')
    .replace(/[^\d,.]/g, '')
    // Binlik ayırıcı noktayı at: "44.000" -> "44000"
    .replace(/\.(?=\d{3}(?:\D|$))/g, '')
    .replace(',', '.');

const amountHaystack = (match) => {
  const values = [
    match?.invoiceDraft?.invoiceTotals?.payableAmount,
    match?.invoiceDraft?.amount,
    match?.totalSelectedAmount,
    match?.bankMovementData?.amount
  ]
    .map((v) => Number(v))
    .filter((v) => Number.isFinite(v) && v > 0);
  // Hem tam hem yuvarlanmış biçim aranabilsin
  return values.flatMap((v) => [String(v), String(Math.round(v)), v.toFixed(2)]);
};

const digitsOf = (value) => String(value || '').replace(/\D/g, '');

/** Bir eşleşme kaydının aranabilir metin parçaları. */
export const buildInvoiceSearchHaystack = (match) => {
  const draft = match?.invoiceDraft || {};
  const islemNos = [
    match?.islemData?.islemNo,
    ...(Array.isArray(match?.islemlerData) ? match.islemlerData.map((r) => r?.islemNo) : [])
  ];

  const texts = [
    draft.customerName,
    draft.title,
    draft.productName,
    draft.invoiceNumber,
    draft.taxOffice,
    resolveMovementSenderName(match?.bankMovementData),
    resolveIslemMusteriName(match?.islemData),
    ...islemNos
  ]
    .filter(Boolean)
    .map((t) => normalizeSearchText(t));

  const digits = [draft.vknTckn, draft.customerTc, draft.customerTaxNo, draft.customerPhone, ...islemNos]
    .map(digitsOf)
    .filter(Boolean);

  return { texts, digits, amounts: amountHaystack(match) };
};

/**
 * Arama kutusu eşleşmesi. Boş sorgu her kaydı geçirir.
 * Çok kelimeli sorguda tüm kelimeler eşleşmeli ("ayşe 12500" gibi).
 */
export const matchesInvoiceSearch = (match, query) => {
  const raw = String(query || '').trim();
  if (!raw) return true;

  const hay = buildInvoiceSearchHaystack(match);
  const terms = normalizeSearchText(raw).split(' ').filter(Boolean);

  return terms.every((term) => {
    if (hay.texts.some((t) => t.includes(term))) return true;

    const termDigits = term.replace(/\D/g, '');
    if (termDigits.length >= 3 && hay.digits.some((d) => d.includes(termDigits))) return true;

    const amountTerm = normalizeAmountQuery(term);
    if (amountTerm && /\d/.test(amountTerm) && hay.amounts.some((a) => a.startsWith(amountTerm))) {
      return true;
    }
    return false;
  });
};

/** Listeyi arama sorgusuna göre süzer. */
export const filterMatchesBySearch = (matches, query) => {
  if (!String(query || '').trim()) return matches || [];
  return (matches || []).filter((m) => matchesInvoiceSearch(m, query));
};

export const getMatchStatus = (score) => {
  if (score >= INVOICE_MATCH_THRESHOLDS.matched) return INVOICE_MATCH_STATUS.MATCHED;
  if (score >= INVOICE_MATCH_THRESHOLDS.suspicious) return INVOICE_MATCH_STATUS.SUSPICIOUS;
  return INVOICE_MATCH_STATUS.UNMATCHED;
};

/** Öneri satırında gösterilecek isim güveni etiketi. */
export const nameMatchLevelLabel = (level) => {
  switch (level) {
    case NAME_MATCH_LEVEL.STRONG:
      return 'İsim eşleşti';
    case NAME_MATCH_LEVEL.MEDIUM:
      return 'İsim kısmen eşleşti';
    case NAME_MATCH_LEVEL.UNKNOWN:
      return 'İşlemde ad yok · kontrol edin';
    default:
      return '';
  }
};

export const matchStatusLabel = (status) => {
  if (status === INVOICE_MATCH_STATUS.MATCHED) return 'Eşleşti';
  if (status === INVOICE_MATCH_STATUS.SUSPICIOUS) return 'Şüpheli';
  return 'Eşleşmedi';
};

export const matchStatusColor = (status) => {
  if (status === INVOICE_MATCH_STATUS.MATCHED) return 'success';
  if (status === INVOICE_MATCH_STATUS.SUSPICIOUS) return 'warning';
  return 'default';
};

const scoreDateAndTime = (bankDate, islemDate) => {
  if (!bankDate || !islemDate) return { date: 0, time: 0 };
  const dayBank = startOfDay(bankDate);
  const dayIslem = startOfDay(islemDate);
  const dayDiff = Math.abs(dayBank - dayIslem) / 86400000;

  let date = 0;
  if (dayDiff === 0) date = DATE_SAME_DAY;
  else if (dayDiff <= 1) date = DATE_ONE_DAY;
  else if (dayDiff <= 3) date = DATE_THREE_DAYS;

  let time = 0;
  if (dayDiff === 0) {
    const hourDiff = Math.abs(bankDate.getTime() - islemDate.getTime()) / 3600000;
    if (hourDiff <= 1) time = TIME_ONE_HOUR;
    else if (hourDiff <= 3) time = TIME_THREE_HOURS;
    else if (hourDiff <= 6) time = TIME_SIX_HOURS;
  }

  return { date, time };
};

const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

/**
 * Tek banka hareketi ↔ tek işlem eşleşme skoru (0–100).
 */
export const calculateMatchScore = (bankMovement, islem) => {
  const bankAmount = Number(bankMovement?.amount) || 0;
  const islemAmount = Number(islem?.havaleEft) || 0;
  const bankDate = resolveFirestoreDate(bankMovement?.bookingDate || bankMovement?.valueDate || bankMovement?.createdAt);
  const islemDate = resolveFirestoreDate(islem?.timestamp);

  const amountMatch = isInvoiceAmountMatch(bankAmount, islemAmount);
  const amountScore = amountMatch ? AMOUNT_EXACT : 0;
  const { date, time } = scoreDateAndTime(bankDate, islemDate);

  const senderName = resolveMovementSenderName(bankMovement);
  const customerName = resolveIslemMusteriName(islem);
  let nameEval = evaluateInvoiceNameMatch(senderName, customerName);

  // Hesap sahibi ile malın sahibi farklı olabilir: havaleyi "MEHMET DEMİRSOY"
  // gönderir, açıklamada "FAST...-BÜŞRA ALTINTAŞ-ELDEN TESLİM EDİLEN..." yazar.
  // Gönderen adı tutmadığında açıklamadaki isimlere de bakılır. Tutar ve gün
  // koşulları aynen geçerli olduğu için bu yalnızca KAÇAN eşleşmeyi yakalar.
  if (!nameEval.nameMatch) {
    const digerAdlar = resolveMovementSenderNameCandidates(bankMovement).filter(
      (ad) => ad && ad !== senderName
    );
    for (const ad of digerAdlar) {
      const alternatif = evaluateInvoiceNameMatch(ad, customerName);
      if (alternatif.nameMatch) {
        nameEval = alternatif;
        break;
      }
    }
  }

  const { nameMatch, nameMatchLevel, nameScore, strength: nameStrength } = nameEval;

  let total = amountScore + date + time + (nameMatch ? nameScore : 0);

  // İşlemde müşteri adı hiç girilmemişse (yer tutucu) bu bir ÇELİŞKİ değil.
  // Tutar ve gün birebir tutuyorsa personele öneri olarak gösterilir; ama isim
  // doğrulanmadığı için skor otomatik eşleşme eşiğinin (80) altında kalır.
  if (amountMatch && !nameMatch && isPlaceholderCustomerName(customerName) && date === DATE_SAME_DAY) {
    return {
      score: total,
      status: INVOICE_MATCH_STATUS.SUSPICIOUS,
      amountMatch,
      nameMatch: false,
      nameMatchLevel: NAME_MATCH_LEVEL.UNKNOWN,
      breakdown: { amount: amountScore, date, time, name: 0, nameStrength: 'unknown' }
    };
  }

  if (!amountMatch || !nameMatch) {
    total = Math.min(total, 40);
    return {
      score: total,
      status: INVOICE_MATCH_STATUS.UNMATCHED,
      amountMatch,
      nameMatch,
      nameMatchLevel,
      breakdown: { amount: amountScore, date, time, name: nameMatch ? nameScore : 0, nameStrength }
    };
  }

  let status = INVOICE_MATCH_STATUS.UNMATCHED;
  if (nameMatchLevel === NAME_MATCH_LEVEL.STRONG) {
    status = getMatchStatus(total);
  } else if (nameMatchLevel === NAME_MATCH_LEVEL.MEDIUM) {
    status =
      total >= INVOICE_MATCH_THRESHOLDS.suspicious
        ? INVOICE_MATCH_STATUS.SUSPICIOUS
        : INVOICE_MATCH_STATUS.UNMATCHED;
  }

  return {
    score: total,
    status,
    amountMatch,
    nameMatch,
    nameMatchLevel,
    breakdown: { amount: amountScore, date, time, name: nameScore, nameStrength }
  };
};

/** Aynı isimde çok sayıda işlem olduğunda arama patlamasın diye aday tavanı. */
const MULTI_MATCH_MAX_CANDIDATES = 16;
const TOLERANCE = 0.02;

/**
 * Hedef tutara ulaşan işlem kombinasyonları.
 * Adaylar tutara göre sıralanır ve toplam hedefi aştığında dal kesilir; budama olmadan
 * arama n^maxSize mertebesine çıkıp aynı isimli kalabalık listelerde arayüzü kilitliyordu.
 */
const islemAmountOf = (item) => Number(item?.havaleEft) || 0;

const findAmountCombinations = (items, targetAmount, minSize, maxSize, amountOf = islemAmountOf) => {
  const sorted = [...items]
    .filter((item) => amountOf(item) > 0)
    .sort((a, b) => amountOf(b) - amountOf(a))
    .slice(0, MULTI_MATCH_MAX_CANDIDATES);

  // suffixSums[i] = i'den sonuna kalan tüm tutarların toplamı (yetersiz kalan dalı keser).
  const suffixSums = new Array(sorted.length + 1).fill(0);
  for (let i = sorted.length - 1; i >= 0; i -= 1) {
    suffixSums[i] = suffixSums[i + 1] + amountOf(sorted[i]);
  }

  const results = [];
  const combo = [];

  const search = (start, sum) => {
    if (results.length >= 20) return;
    if (combo.length >= minSize && Math.abs(sum - targetAmount) < TOLERANCE) {
      results.push([...combo]);
      return;
    }
    if (combo.length >= maxSize) return;
    if (sum > targetAmount + TOLERANCE) return;
    if (sum + suffixSums[start] < targetAmount - TOLERANCE) return;

    for (let index = start; index < sorted.length; index += 1) {
      const amount = amountOf(sorted[index]);
      if (sum + amount > targetAmount + TOLERANCE) continue;
      combo.push(sorted[index]);
      search(index + 1, sum + amount);
      combo.pop();
    }
  };

  search(0, 0);
  return results;
};

/** Aynı isim, farklı tutar — çoklu eşleştirme ipucu. */
export const findSameNameDifferentAmountIslemler = (movement, islemler) => {
  const sender = resolveMovementSenderName(movement);
  return (islemler || []).filter((islem) => {
    const nameEval = evaluateInvoiceNameMatch(sender, resolveIslemMusteriName(islem));
    return nameEval.nameMatch && !isInvoiceAmountMatch(movement?.amount, islem?.havaleEft);
  });
};

/** Banka hareketi için olası çoklu eşleştirme kombinasyonu. */
export const suggestMultiMatchForMovement = (movement, islemler, { maxComboSize = 5, maxDayDiff = 7 } = {}) => {
  if (!movement || !Array.isArray(islemler)) return null;

  const bankAmount = Number(movement?.amount) || 0;
  if (bankAmount <= 0) return null;

  const bankDate = resolveMovementListDate(movement);
  const sender = resolveMovementSenderName(movement);

  const candidates = islemler
    .map((islem) => ({
      islem,
      nameEval: evaluateInvoiceNameMatch(sender, resolveIslemMusteriName(islem))
    }))
    .filter(({ nameEval }) => nameEval.nameMatch);

  if (candidates.length < 2) return null;

  const dateFiltered = candidates.filter(({ islem }) => {
    if (!bankDate) return true;
    const islemDate = resolveIslemListDate(islem);
    if (!islemDate) return true;
    const dayDiff = Math.abs(startOfDay(bankDate) - startOfDay(islemDate)) / 86400000;
    return dayDiff <= maxDayDiff;
  });

  if (dateFiltered.length < 2) return null;

  const items = dateFiltered.map(({ islem }) => islem);
  const combos = findAmountCombinations(items, bankAmount, 2, Math.min(maxComboSize, items.length));
  if (!combos.length) return null;

  combos.sort((a, b) => a.length - b.length);
  const best = combos[0];
  const total = best.reduce((sum, row) => sum + Number(row?.havaleEft || 0), 0);

  return {
    islemler: best,
    total,
    islemNos: best.map((row) => row.islemNo || row.id),
    islemIds: best.map((row) => row.id)
  };
};

/**
 * Hareketin olası karşı taraf adları.
 *
 * Banka bazen `senderName` alanına gönderenin adını değil ödeme notunu yazıyor
 * ("Altın ödemesi"), oysa gerçek isim açıklamada duruyor
 * ("FAST2447961-NURSENA ÖZÇELİK-Altın ödemesi"). Bu yüzden açıklamadan
 * ayrıştırılan ad da aday olarak değerlendirilir.
 *
 * DİKKAT: yalnızca çok-hareket eşleştirmesinde kullanılır; tekli eşleştirmenin
 * skorlaması bilerek eski davranışında bırakıldı.
 */
export const resolveMovementSenderNameCandidates = (movement) => {
  const out = [];
  const primary = resolveMovementSenderName(movement);
  if (primary && primary !== UNKNOWN_MOVEMENT_SENDER) out.push(primary);
  const parsed = parseSenderFromDescription(movement?.description);
  if (parsed && !out.includes(parsed)) out.push(parsed);
  parseCustomerNamesFromDescription(movement?.description).forEach((name) => {
    if (name && !out.includes(name)) out.push(name);
  });
  return out;
};

/** Adaylardan herhangi biri müşteri adıyla eşleşiyor mu. */
const movementMatchesCustomerName = (movement, customerName) =>
  resolveMovementSenderNameCandidates(movement).some(
    (name) => evaluateInvoiceNameMatch(name, customerName).nameMatch
  );

/**
 * Tek işlem ↔ ÇOK banka hareketi.
 *
 * Motor bugüne kadar yalnızca tersini yapıyordu (bir hareket ↔ çok işlem).
 * Müşteri tek satışın bedelini birden fazla havaleyle gönderdiğinde
 * (ör. 100.000 × 4 + 47.000 = 447.000) hiçbir hareket tek başına işlemin
 * tutarını tutturamadığı için eşleşme hiç oluşmuyordu.
 *
 * Koşullar bilinçli olarak dar: aynı müşteri adı, tam tutar, tarih yakınlığı.
 * @returns {{ movements: object[], total: number, movementIds: string[] }|null}
 */
export const suggestMultiMovementMatchForIslem = (
  islem,
  movements,
  { maxComboSize = 6, maxDayDiff = 7 } = {}
) => {
  if (!islem || !Array.isArray(movements)) return null;

  const target = Number(islem?.havaleEft) || 0;
  if (target <= 0) return null;

  const customerName = resolveIslemMusteriName(islem);
  if (!customerName) return null;

  const islemDate = resolveIslemListDate(islem);

  const candidates = movements.filter((movement) => {
    if (!movement?.id) return false;
    if (!movementMatchesCustomerName(movement, customerName)) return false;
    if (!islemDate) return true;
    const movementDate = resolveMovementListDate(movement);
    if (!movementDate) return true;
    const dayDiff = Math.abs(startOfDay(islemDate) - startOfDay(movementDate)) / 86400000;
    return dayDiff <= maxDayDiff;
  });

  if (candidates.length < 2) return null;

  const amountOf = (movement) => Number(movement?.amount) || 0;
  const combos = findAmountCombinations(
    candidates,
    target,
    2,
    Math.min(maxComboSize, candidates.length),
    amountOf
  );
  if (!combos.length) return null;

  combos.sort((a, b) => a.length - b.length);
  const best = combos[0];
  return {
    movements: best,
    total: best.reduce((sum, m) => sum + amountOf(m), 0),
    movementIds: best.map((m) => String(m.id))
  };
};

/**
 * Seçili banka hareketi için en iyi işlem önerileri.
 */
export const suggestIslemlerForMovement = (movement, islemler, { minScore = INVOICE_MATCH_THRESHOLDS.suspicious, limit = 8 } = {}) => {
  if (!movement || !Array.isArray(islemler)) return [];
  return islemler
    .map((islem) => {
      const match = calculateMatchScore(movement, islem);
      return { islem, ...match };
    })
    .filter((row) => isInvoicePairEligibleForSuggestion(row) && row.score >= minScore)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);
};

/**
 * Seçili işlem için en iyi banka hareketi önerileri.
 */
export const suggestMovementsForIslem = (islem, movements, { minScore = INVOICE_MATCH_THRESHOLDS.suspicious, limit = 8 } = {}) => {
  if (!islem || !Array.isArray(movements)) return [];
  return movements
    .map((movement) => {
      const match = calculateMatchScore(movement, islem);
      return { movement, ...match };
    })
    .filter((row) => isInvoicePairEligibleForSuggestion(row) && row.score >= minScore)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);
};

/**
 * Tüm gelen banka hareketleri için özet eşleşme durumu.
 */
export const summarizeMovementMatches = (movements, islemler) => {
  const incoming = filterIncomingBankMovements(movements);
  const eligibleIslemler = filterInvoiceEligibleIslemler(islemler);
  let matched = 0;
  let suspicious = 0;
  let unmatched = 0;

  incoming.forEach((movement) => {
    const best = suggestIslemlerForMovement(movement, eligibleIslemler, { minScore: INVOICE_MATCH_THRESHOLDS.matched, limit: 1 })[0];
    const status = best?.status || INVOICE_MATCH_STATUS.UNMATCHED;
    if (status === INVOICE_MATCH_STATUS.MATCHED) matched += 1;
    else if (status === INVOICE_MATCH_STATUS.SUSPICIOUS) suspicious += 1;
    else unmatched += 1;
  });

  return { matched, suspicious, unmatched, total: incoming.length };
};
